import { Group, Loader, Stack } from '@mantine/core';
import type { CatalogRule, Filter } from '@repo/api-codegen';
import {
	getExcludedFromQualityGovernanceQueryKey,
	getIncludedInQualityGovernanceQueryKey,
	useGetExcludedFromQualityGovernance,
	useGetIncludedInQualityGovernance,
	useSetExcludedFromQualityGovernance,
	useSetIncludedInQualityGovernance,
} from '@repo/api-codegen/v2';
import { Text, Title } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import { queryClient } from '../../../api';
import { DEFAULT_FILTER_OPTIONS, FILTER_OPTIONS_DIVIDER } from '../../Filter';
import { FILTER_OPTIONS_CONFIG } from '../../Filter/constants';
import { GovernanceFilters } from './GovernanceFilters';

export function QualityGovernanceFilters() {
	const { data: includedFilters, isLoading: isLoadingIncluded } =
		useGetIncludedInQualityGovernance({});

	const { mutateAsync: updateIncludedFilters } =
		useSetIncludedInQualityGovernance({
			onSuccess: (_, variables) => {
				// optimistic update
				queryClient.setQueryData(
					getIncludedInQualityGovernanceQueryKey({}),
					variables.body
				);

				// invalidate all other quality queries other than governance
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey?.[0] === 'quality' &&
						!query.queryKey.includes('governance'),
				});
			},
		});

	const { data: excludedFilters, isLoading: isLoadingExcluded } =
		useGetExcludedFromQualityGovernance({});
	const { mutateAsync: updateExcludedFilters } =
		useSetExcludedFromQualityGovernance({
			onSuccess: (_, variables) => {
				// optimistic update
				queryClient.setQueryData(
					getExcludedFromQualityGovernanceQueryKey({}),
					variables.body
				);

				// invalidate all other quality queries other than governance
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey?.[0] === 'quality' &&
						!query.queryKey.includes('governance'),
				});
			},
		});

	// This must match the backend implementation
	const filter: Filter = useMemo(
		() => ({
			operator: 'and',
			operands: [
				...(includedFilters ? [includedFilters] : []),
				...(excludedFilters?.operands.length &&
				excludedFilters.operands.length > 0
					? [{ operator: 'not' as const, operands: [excludedFilters] }]
					: []),
			],
		}),
		[includedFilters, excludedFilters]
	);

	const handleOnIncludedFiltersChange = useCallback(
		async (rule: CatalogRule) => {
			await updateIncludedFilters({ body: rule });
		},
		[updateIncludedFilters]
	);

	const handleOnExcludedFiltersChange = useCallback(
		async (rule: CatalogRule) => {
			await updateExcludedFilters({ body: rule });
		},
		[updateExcludedFilters]
	);

	if (
		isLoadingIncluded ||
		isLoadingExcluded ||
		!includedFilters ||
		!excludedFilters
	) {
		return (
			<Group position="center">
				<Loader />
			</Group>
		);
	}

	return (
		<Stack spacing="md">
			<Title size="md">Resource access</Title>
			<Text size="sm" color="text/secondary/default" mb="md">
				Create rules to include and exclude resources from Data Quality Score
			</Text>
			<GovernanceFilters
				previewFilter={filter}
				emptyStateMessage="Currently, DQS is enabled on all resources in your workspace. Add filters to limit the scope."
				includedFilters={includedFilters}
				excludedFilters={excludedFilters}
				onIncludedFiltersChange={handleOnIncludedFiltersChange}
				onExcludedFiltersChange={handleOnExcludedFiltersChange}
				filterOptions={DEFAULT_FILTER_OPTIONS.map((option) =>
					option === FILTER_OPTIONS_DIVIDER
						? option
						: FILTER_OPTIONS_CONFIG[option]
				)}
			/>
		</Stack>
	);
}
